import { Side } from '../routes/Play';

function getRandomElement(arr: any[]) {
    return arr[Math.floor(Math.random() * arr.length)];
}

export class Node {
    constructor(
        public state: (Side | null)[],
        public value: number | undefined = undefined,
        public alpha: number = 0,
        public beta: number = 0,
        public children: Node[] = [],
        public parent: Node | undefined = undefined
    ) {}
}

export class Tree {
    private originalRoot: Node;

    constructor(public root: Node, public maxSide: Side) {
        this.originalRoot = this.root;
    }

    reset() {
        this.root = this.originalRoot;
    }

    play(state: (Side | null)[]) {
        const node = this.root.children.find(
            (n) => n.state.join(',') === state.join(',')
        );
        if (node) {
            this.root = node;
        } else {
            throw Error('Play not possible: ' + state);
        }
    }

    nextAIMove() {
        // find the child with same value as the root
        let value = this.root.value;
        value = Math.max(...this.root.children.map((n) => n.value as number));
        const nodes = this.root.children.filter((n) => n.value === value);
        return getRandomElement(nodes);
    }

    generate(turn: Side, node?: Node) {
        if (!node) {
            node = this.root;
        }

        node.value = this.evaluate(node.state);

        // if the node is not a leaf node
        if (node.value === undefined) {
            // generate children
            for (let i = 0; i < node.state.length; i++) {
                if (node.state[i] === null) {
                    let newNode = new Node(
                        node.state.map((o, j) => (j === i ? turn : o)),
                        undefined,
                        node.alpha,
                        node.beta,
                        [],
                        node
                    );
                    node.children.push(newNode);
                }
            }

            // recursively generate children of children
            const newTurn = turn === 'x' ? 'o' : 'x';
            for (let child of node.children) {
                this.generate(newTurn, child);
            }

            // set the value of the node
            if (turn === this.maxSide) {
                node.value = Math.max(
                    ...node.children.map((child) => child.value || 0)
                );
            } else {
                node.value = Math.min(
                    ...node.children.map((child) => child.value || 0)
                );
            }
        } else {
            // modify the alpha and beta of parent node
            if (node.parent !== undefined) {
                if (turn === this.maxSide) {
                    node.parent.beta = Math.min(node.beta, node.value);
                } else {
                    node.parent.alpha = Math.max(node.alpha, node.value);
                }
            }
        }
    }

    private checkWinCondition(board: (Side | null)[]) {
        // check rows for a win condition
        for (let i = 0; i < 9; i += 3) {
            if (
                board[i] === board[i + 1] &&
                board[i] === board[i + 2] &&
                board[i + 1] === board[i + 2] &&
                board[i] !== null
            ) {
                return board[i] as Side;
            }
        }

        // check columns for a win condition
        for (let i = 0; i < 3; i++) {
            if (
                board[i] === board[i + 3] &&
                board[i] === board[i + 6] &&
                board[i + 3] === board[i + 6] &&
                board[i] !== null
            ) {
                return board[i] as Side;
            }
        }

        // check both diagonals for a win condition
        if (
            board[0] === board[4] &&
            board[0] === board[8] &&
            board[4] === board[8] &&
            board[0] !== null
        ) {
            return board[0] as Side;
        }
        if (
            board[2] === board[4] &&
            board[2] === board[6] &&
            board[4] === board[6] &&
            board[2] !== null
        ) {
            return board[2] as Side;
        }

        if (board.every((cell) => cell !== null)) {
            return null;
        }

        return undefined;
    }

    private evaluate(state: (Side | null)[]): number | undefined {
        const minSide = this.maxSide === 'x' ? 'o' : 'x';
        let winner = this.checkWinCondition(state);
        if (winner === this.maxSide) {
            return 10;
        } else if (winner === minSide) {
            return -10;
        } else if (winner === null) {
            return 0;
        } else {
            return undefined;
        }
    }
}
